// src/hooks/useChats.js
//
// Chat v2 inbox subscription. Returns the current user's chats
// ordered by lastMessageAt desc, with unread state denormalized
// against per-chat /reads/{uid} read receipts.
//
// Used by:
//   - InboxV2 page (the main /messages list)
//   - Bottom tab + sidebar badges (via useChatUnreadCount which
//     calls this under the hood)

import { useEffect, useState } from 'react'
import {
  collection, query, where, orderBy, onSnapshot,
  doc,
} from 'firebase/firestore'
import { db } from '@/firebase/firebase'

function toMillis(ts) {
  if (!ts) return 0
  if (typeof ts.toMillis === 'function') return ts.toMillis()
  if (typeof ts === 'number') return ts
  if (ts._seconds != null) return ts._seconds * 1000
  return 0
}

/**
 * Subscribe to all chats the user is a participant in.
 *
 * Returns each chat with:
 *   - id, ...all chat fields
 *   - isUnread: bool (lastMessageAt > my lastReadAt, AND last message
 *     wasn't sent by me)
 *   - lastReadAt: my read-receipt timestamp (ms) or 0
 *
 * @param {string|null} uid current user's Firebase Auth uid
 */
export default function useChats(uid) {
  const [chats, setChats] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // 1. Subscribe to the chat list
  useEffect(() => {
    if (!uid) {
      setChats([])
      setLoading(false)
      return
    }
    setLoading(true)

    const q = query(
      collection(db, 'chats'),
      where('participantIds', 'array-contains', uid),
      orderBy('lastMessageAt', 'desc'),
    )

    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }))
        setChats(list)
        setLoading(false)
      },
      (err) => {
        // Common cause: missing composite index. Print so it's
        // diagnosable in the console; don't crash the UI.
        console.warn('[useChats] snapshot error:', err?.code, err?.message)
        setError(err?.message || 'chat-list-failed')
        setLoading(false)
      },
    )
    return () => unsub()
  }, [uid])

  // 2. Subscribe to per-chat /reads/{uid} so we can compute isUnread
  //    in real time. We watch each chat's read doc individually so
  //    marking-as-read in one tab updates badges in all tabs without
  //    forcing a full chat-list re-fetch.
  const [readByChat, setReadByChat] = useState({})
  useEffect(() => {
    if (!uid || chats.length === 0) {
      setReadByChat({})
      return
    }
    const unsubs = chats.map((c) =>
      onSnapshot(
        doc(db, 'chats', c.id, 'reads', uid),
        (snap) => {
          const data = snap.exists() ? snap.data() : null
          setReadByChat((prev) => ({
            ...prev,
            [c.id]: toMillis(data?.lastReadAt),
          }))
        },
        () => {
          // Read doc may not exist yet for newly added chats. Treat
          // as never-read (lastReadAt = 0) so the chat lights up as
          // unread until the user opens it.
          setReadByChat((prev) => ({ ...prev, [c.id]: 0 }))
        },
      )
    )
    return () => unsubs.forEach((u) => u && u())
  }, [uid, chats.map((c) => c.id).join(',')])

  // 3. Decorate each chat with isUnread + lastReadAt
  const decorated = chats.map((c) => {
    const lastReadAt = readByChat[c.id] || 0
    const lastMessageAt = toMillis(c.lastMessageAt)
    // I sent the last message? Then it's not unread "for me".
    const sentByMe = c.lastMessage?.senderId === uid
    const isUnread = !sentByMe && lastMessageAt > 0 && lastMessageAt > lastReadAt
    return { ...c, isUnread, lastReadAt }
  })

  return { chats: decorated, loading, error }
}
